/* tslint:disable object-literal-sort-keys */
import React from 'react';
import { get, keyBy } from 'lodash';

import { TableRenderingUtils } from '../../../utils';
import { ILoanComplianceCharge, ILoanPayment, IReactTableColumn, IReactTableItem } from '../../../interfaces';

const { renderDate, renderMoney, renderSummedAmount, renderTotalRowCount, renderUnformatted } = TableRenderingUtils;

/**
 * A charge is an estimate, settled, overdue, or simply not due yet. The API works all four out, so
 * the table reads them off the record rather than comparing dates itself.
 *
 * 'Estimate' comes first because it is the one status that says the number beside it can still
 * change: the quarter is still running and the API is re-pricing the charge nightly on what the
 * account currently owes. It is also why that row offers no Log Payment -- the API refuses payment
 * against a charge whose amount is not final, so without this the row looks arbitrarily locked.
 *
 * 'Unpaid' rather than 'Outstanding' for the last of the four: it has to fit the column, and the
 * longer word reads as a heading for the Outstanding amount sitting immediately to its left.
 */
function renderStatus (item: IReactTableItem) {
  const charge = item.original as ILoanComplianceCharge;

  let status = 'unpaid'
    , label = 'Unpaid'
    ;

  if (!charge.is_amount_final) { status = 'estimate'; label = 'Estimate'; }
  else if (charge.is_paid) { status = 'paid'; label = 'Paid'; }
  else if (charge.is_overdue) { status = 'overdue'; label = 'Overdue'; }

  // .ellipsis to match renderFormattedCell, which every other cell in these tables goes through
  return <div className='ellipsis'><span className={`compliance-status ${status}`}>{label}</span></div>;
}

// Sorting the raw is_paid accessor interleaves Unpaid and Overdue, which is the one grouping the
// column is there to produce. Ranked instead, running from nothing to do about it to act on it now,
// so a descending sort brings the overdue rows together at the top.
const STATUS_RANK = { estimate: 0, paid: 1, unpaid: 2, overdue: 3 };

function statusRank (charge: ILoanComplianceCharge) {
  if (!charge.is_amount_final) { return STATUS_RANK.estimate; }
  if (charge.is_paid) { return STATUS_RANK.paid; }
  if (charge.is_overdue) { return STATUS_RANK.overdue; }

  return STATUS_RANK.unpaid;
}

const COMPLIANCE_CHARGE_COLUMNS: IReactTableColumn[] = [
  {
    // The start of the quarter charged for. With 'Due' beside it the row reads as the span the
    // charge covers, which is what it is: a period runs from here to its due date, and the amount is
    // the principal outstanding at the end of it.
    Header: 'Period Start',
    columns: [{
      accessor: 'period_start_date',
      Cell: renderDate,
      minWidth: 90,
      maxWidth: 140,
      sortable: true,
    }],
  },
  {
    Header: 'Due',
    columns: [{
      Header: renderTotalRowCount,
      accessor: 'due_date',
      Cell: renderDate,
      minWidth: 90,
      maxWidth: 140,
      sortable: true,
    }],
  },
  {
    // The widest header of the seven, so it sets the floor that keeps it off an ellipsis
    Header: 'Principal Charged',
    columns: [{
      accessor: 'principal_basis_amount',
      Cell: renderMoney,
      minWidth: 165,
      maxWidth: 220,
    }],
  },
  {
    Header: 'Charge',
    columns: [{
      Header: renderSummedAmount,
      accessor: 'amount',
      Cell: renderMoney,
      minWidth: 100,
      maxWidth: 160,
    }],
  },
  {
    Header: 'Paid',
    columns: [{
      Header: renderSummedAmount,
      accessor: 'amount_paid',
      Cell: renderMoney,
      minWidth: 90,
      maxWidth: 160,
    }],
  },
  {
    Header: 'Outstanding',
    columns: [{
      Header: renderSummedAmount,
      accessor: 'amount_outstanding',
      Cell: renderMoney,
      minWidth: 120,
      maxWidth: 170,
    }],
  },
  {
    // Wide enough for 'Overdue', the longest of the three the cell can render
    Header: 'Status',
    columns: [{
      id: 'status',
      accessor: statusRank,
      Cell: renderStatus,
      minWidth: 95,
      maxWidth: 150,
    }],
  },
];

/**
 * Built from the account's charges rather than declared flat, so a payment can name the quarter it
 * settled. Without it the two tables can't be reconciled: an account carrying several outstanding
 * quarters shows a bare amount here with nothing tying it to one of them.
 *
 * Identifying a charge by its due date matches the modal's option labels.
 */
function compliancePaymentColumns (charges: ILoanComplianceCharge[]): IReactTableColumn[] {
  const chargesById = keyBy(charges, 'id');

  // Resolved through the accessor rather than in the Cell so that the column sorts by the date it
  // shows. Reading the raw field would sort the rows by charge id, which looks like a broken date
  // sort. renderDate handles the miss, giving EMPTY_FIELD like every other cell here.
  function chargeDueDate (payment: ILoanPayment) {
    return get(chargesById[payment.compliance_charge as string], 'due_date');
  }

  return [
    {
      Header: 'Payment Date',
      columns: [{
        Header: renderTotalRowCount,
        accessor: 'payment_date',
        Cell: renderDate,
        minWidth: 120,
        maxWidth: 200,
        sortable: true,
      }],
    },
    {
      Header: 'Amount',
      columns: [{
        Header: renderSummedAmount,
        accessor: 'amount',
        Cell: renderMoney,
        minWidth: 110,
        maxWidth: 200,
      }],
    },
    {
      Header: 'Charge Due',
      columns: [{
        id: 'charge_due_date',
        accessor: chargeDueDate,
        Cell: renderDate,
        minWidth: 120,
        maxWidth: 200,
        sortable: true,
      }],
    },
    {
      Header: 'Notes',
      columns: [{
        accessor: 'notes',
        Cell: renderUnformatted,
        minWidth: 180,
      }],
    },
  ];
}

export { COMPLIANCE_CHARGE_COLUMNS, compliancePaymentColumns };
